import { useEffect } from "react";
import styled, { keyframes } from "styled-components";

// Outer ring rotation
const rotateOuter = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Inner ring rotation (opposite direction)
const rotateInner = keyframes`
  0% { transform: rotate(360deg); }
  100% { transform: rotate(0deg); }
`;

// Floating animation
const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
  50% { transform: translateY(-15px) rotate(3deg) scale(1.05); }
`;

// Glow pulse
const glow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.4)); }
  50% { filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.6)); }
`;

// Text reveal
const textReveal = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const LoaderWrapper = styled.div`
  min-height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  perspective: 1000px;
  padding: 0;
`;

const LoaderContainer = styled.div`
  position: relative;
  width: min(85vw, 200px);
  height: min(85vw, 200px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
`;

const OuterRing = styled.div`
  position: absolute;
  width: min(75vw, 180px);
  height: min(75vw, 180px);
  border-radius: 50%;
  border: min(0.4vw, 3px) solid transparent;
  border-top: min(0.4vw, 3px) solid #e94560;
  border-right: min(0.4vw, 3px) solid #533483;
  animation: ${rotateOuter} 3s linear infinite;
  box-shadow: 0 0 15px rgba(233, 69, 96, 0.2);
`;

const InnerRing = styled.div`
  position: absolute;
  width: min(55vw, 140px);
  height: min(55vw, 140px);
  border-radius: 50%;
  border: min(0.4vw, 3px) solid transparent;
  border-bottom: min(0.4vw, 3px) solid #e94560;
  border-left: min(0.4vw, 3px) solid #533483;
  animation: ${rotateInner} 2s linear infinite;
  box-shadow: 0 0 15px rgba(83, 52, 131, 0.2);
`;

const TruckContainer = styled.div`
  position: absolute;
  font-size: min(15vw, 3.5rem);
  animation: ${float} 3s ease-in-out infinite, ${glow} 2s ease-in-out infinite;
  transform-style: preserve-3d;
  z-index: 2;
  margin-bottom: min(8vw, 40px);
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
`;

const LoaderText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: min(3.8vw, 1.4rem);
  font-weight: 800;
  letter-spacing: min(0.2vw, 0.04em);
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  z-index: 3;
  animation: ${textReveal} 1s ease-out forwards;
  opacity: 0;
  background: linear-gradient(90deg, #e94560, #533483);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  padding: min(0.6vw, 0.3rem) min(1.2vw, 0.6rem);
  border-radius: min(1.2vw, 6px);
  backdrop-filter: blur(5px);
  text-align: center;
  width: min(90vw, 200px);
  max-width: 90vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.1;
  margin: 0 auto;

  @media (max-width: 360px) {
    font-size: min(3.5vw, 1.2rem);
    letter-spacing: min(0.15vw, 0.03em);
    padding: min(0.5vw, 0.25rem) min(1vw, 0.5rem);
    width: min(95vw, 180px);
  }
`;

const Loader = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <LoaderWrapper>
      <LoaderContainer>
        <OuterRing />
        <InnerRing />
        <TruckContainer>🚚</TruckContainer>
        <LoaderText>National India Roadways</LoaderText>
      </LoaderContainer>
    </LoaderWrapper>
  );
};

export default Loader;